/**
 * Custom hook for broker connection guard logic
 * Encapsulates all broker connection checking logic
 */

import { useEffect, useRef, useState, useMemo } from 'react';
import { useAuthContext } from '../contexts/AuthContext';
import { useBrokerIntegration } from '../contexts/BrokerIntegrationContextCompat';
import {
  checkBypassFlags,
  clearBypassFlags,
  determineAccess,
  logBrokerGuardDecision
} from '../utils/brokerGuardUtils';

/**
 * Custom hook for broker connection guard
 * @returns {Object} - guard state and actions
 */
export const useBrokerConnectionGuard = () => {
  const { user, loading } = useAuthContext();
  const { hasConnections, hasExpiredConnections, hasAnyConnections, loading: brokerLoading } = useBrokerIntegration();
  
  // State management
  const [guardState, setGuardState] = useState({
    isResolved: false,
    shouldRedirect: false,
    redirectPath: null,
    allowAccess: false,
    reason: 'initializing'
  });

  // Refs for preventing multiple redirects
  const redirectedRef = useRef(false);
  const resolvedRef = useRef(false);
  const redirectTimerRef = useRef(null);

  // Connection status object (memoized to prevent unnecessary re-renders)
  const connectionStatus = useMemo(() => ({
    hasConnections,
    hasExpiredConnections,
    hasAnyConnections
  }), [hasConnections, hasExpiredConnections, hasAnyConnections]);

  // Main guard logic
  useEffect(() => {
    const checkBrokerConnection = async () => {
      // Check bypass flags first
      if (checkBypassFlags(sessionStorage)) {
        logBrokerGuardDecision('bypass', { reason: 'bypass_flags_set' });
        clearBypassFlags(sessionStorage);
        setGuardState({
          isResolved: true,
          shouldRedirect: false,
          redirectPath: null,
          allowAccess: true,
          reason: 'bypass'
        });
        resolvedRef.current = true;
        return;
      }

      // Wait for data to load
      if (loading || brokerLoading || resolvedRef.current) {
        return;
      }

      // Determine access based on current state
      const accessDecision = determineAccess(user, connectionStatus, loading, brokerLoading);
      
      logBrokerGuardDecision('access_decision', {
        user: !!user,
        loading,
        brokerLoading,
        connectionStatus,
        decision: accessDecision
      });

      // Handle redirect case with grace period
      if (!accessDecision.allowAccess && accessDecision.reason === 'no_connections') {
        if (!redirectTimerRef.current) {
          redirectTimerRef.current = setTimeout(() => {
            if (!redirectedRef.current && !brokerLoading && !hasConnections && !hasAnyConnections) {
              logBrokerGuardDecision('redirect', { 
                reason: 'no_connections_after_grace_period',
                redirectPath: accessDecision.redirectPath 
              });
              
              setGuardState({
                isResolved: true,
                shouldRedirect: true,
                redirectPath: accessDecision.redirectPath,
                allowAccess: false,
                reason: 'redirect_required'
              });
              
              redirectedRef.current = true;
            }
          }, 2000); // 2s grace period
        }
        return;
      }

      // Handle other cases (allow access)
      if (accessDecision.allowAccess) {
        if (redirectTimerRef.current) {
          clearTimeout(redirectTimerRef.current);
          redirectTimerRef.current = null;
        }
        
        setGuardState({
          isResolved: true,
          shouldRedirect: false,
          redirectPath: null,
          allowAccess: true,
          reason: accessDecision.reason
        });
        
        resolvedRef.current = true;
      }
    };

    checkBrokerConnection();

    // Cleanup function
    return () => {
      if (redirectTimerRef.current) {
        clearTimeout(redirectTimerRef.current);
        redirectTimerRef.current = null;
      }
    };
  }, [user, loading, brokerLoading, connectionStatus]);

  // Reset function for testing
  const resetGuard = () => {
    redirectedRef.current = false;
    resolvedRef.current = false;
    if (redirectTimerRef.current) {
      clearTimeout(redirectTimerRef.current);
      redirectTimerRef.current = null;
    }
    setGuardState({
      isResolved: false,
      shouldRedirect: false,
      redirectPath: null,
      allowAccess: false,
      reason: 'reset'
    });
  };

  return {
    // State
    ...guardState,
    connectionStatus,
    
    // Actions
    resetGuard,
    
    // Debug info
    debug: {
      user: !!user,
      loading,
      brokerLoading,
      hasConnections,
      hasExpiredConnections,
      hasAnyConnections,
      redirected: redirectedRef.current,
      resolved: resolvedRef.current
    }
  };
};
